'use client'
import { memo, useCallback, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Campaign } from '@/types'
import useColorModeValue from '@/hooks/use-color-mode-value'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerDescription,
  DrawerTitle,
} from '@/Components/ui/drawer'
import { ScrollArea } from '@/Components/ui/scroll-area'
import RenderSafeHtml from '../custom/render-safe-html'
import { BorderBeam } from '@/Components/magicui/border-beam'
import { AnimatedGradientText } from '@/Components/magicui/animated-gradient-text'
import useLocalizedValue from '@/hooks/use-localized-value'
import { useTranslation } from 'react-i18next'

const LAYOUT_CLASSES = {
  split: {
    left: 'md:flex-row',
    right: 'md:flex-row-reverse',
  },
  layered: {
    'top-left': 'items-start justify-start text-left pt-16',
    'top-center': 'items-center justify-start text-center pt-16',
    'top-right': 'items-end justify-start text-right pt-16',
    'center-left': 'items-start justify-center text-left',
    'center-center': 'items-center justify-center text-center',
    'center-right': 'items-end justify-center text-right',
    'bottom-left': 'items-start justify-end text-left pb-16',
    'bottom-center': 'items-center justify-end text-center pb-16',
    'bottom-right': 'items-end justify-end text-right pb-16',
  },
}
export default function CampaignSection({ content }: { content: Campaign }) {
  const { layout, mediaOrder, heading, description, hasDetails, media, richContent } = content
  const localizedHeading = useLocalizedValue(heading)
  const localizedDescription = useLocalizedValue(description)
  const localizedRichContent = useLocalizedValue(richContent || {})
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { t } = useTranslation()
  const handleReadMoreClick = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  //   const getLayoutClass = () => {
  //     switch (true) {
  //       case layout === 'split' && mediaOrder === 'left':
  //         return 'md:flex-row'
  //       case layout === 'split' && mediaOrder === 'right':
  //         return 'md:flex-row-reverse'
  //       case layout === 'layered':
  //         switch (mediaOrder) {
  //           case 'top-left':
  //             return 'items-start justify-start text-left pt-16'
  //           case 'top-center':
  //             return 'items-center justify-start text-center pt-16'
  //           case 'top-right':
  //             return 'items-end justify-start text-right pt-16'
  //           case 'center-left':
  //             return 'items-start justify-center text-left'
  //           case 'center-center':
  //             return 'items-center justify-center text-center'
  //           case 'center-right':
  //             return 'items-end justify-center text-right'
  //           case 'bottom-left':
  //             return 'items-start justify-end text-left pb-16'
  //           case 'bottom-center':
  //             return 'items-center justify-end text-center pb-16'
  //           case 'bottom-right':
  //             return 'items-end justify-end text-right pb-16'
  //           default:
  //             return 'items-center justify-center text-center'
  //         }
  //       default:
  //         return 'lg:flex-row'
  //     }
  //   }

  const layoutClass =
    layout === 'split'
      ? LAYOUT_CLASSES.split[mediaOrder as keyof typeof LAYOUT_CLASSES.split] || 'lg:flex-row'
      : layout === 'layered'
        ? LAYOUT_CLASSES.layered[mediaOrder as keyof typeof LAYOUT_CLASSES.layered] ||
          'items-center justify-center text-center'
        : 'lg:flex-row'

  const ContentBlock = memo(() => (
    <div className="flex h-full min-h-0 flex-col max-md:justify-start lg:pr-3">
      <div className="max-w-md space-y-3 xl:space-y-6">
        {heading && (
          <AnimatedGradientText
            speed={0.7}
            colorFrom={useColorModeValue('#15152e', '#e0e0ff')}
            colorTo={useColorModeValue('#f00028', '#f00028')}
            className="text-[clamp(1.25rem,3vw,2.25rem)] font-bold leading-[1.1] md:max-xl:tracking-tighter"
          >
            {localizedHeading}
          </AnimatedGradientText>
        )}
        {description && (
          <div className="relative">
            <p className="line-clamp-4 text-[clamp(0.875rem,2vw,1rem)] leading-[1.1] text-secondary-foreground lg:line-clamp-none">
              {localizedDescription}
            </p>
          </div>
        )}
        <div className="h-[60px] pt-4 lg:h-[72px] lg:pb-4 xl:pt-8">
          {hasDetails && (
            <button
              className="rounded-md bg-primary px-4 py-1 text-white transition-colors hover:bg-primary/90 lg:px-6 lg:py-2"
              onClick={() => handleReadMoreClick()}
            >
             {t('continueReading')}
            </button>
          )}
        </div>
      </div>
    </div>
  ))

  const ReadMoreBlock = memo(() => (
    <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <DrawerContent className="h-[calc(100vh-3rem)] rounded-t-2xl border-none dark:bg-gray-900 lg:rounded-t-3xl">
        <DrawerHeader className="sr-only">
          <DrawerTitle className="sr-only">{localizedHeading}</DrawerTitle>
          <DrawerDescription className="sr-only">{localizedDescription}</DrawerDescription>
        </DrawerHeader>
        <ScrollArea className="pt-4">
          <div className="mx-auto w-full max-w-[1120px] px-6 lg:px-2">
            <RenderSafeHtml
              className={cn(
                'prose prose-base max-w-none dark:prose-invert prose-h2:text-foreground prose-li:marker:text-foreground dark:bg-gray-900/90',
                'px-4 pb-10 prose-h4:leading-tight prose-h5:mb-0.5 prose-h5:font-medium prose-p:leading-snug prose-h5:md:mb-0 prose-h5:md:text-lg [&>p>span>small]:text-[16px]'
              )}
              /** @ts-ignore */
              content={localizedRichContent || richContent || ''}
              stickyFirstImage={true}
            />
          </div>
        </ScrollArea>
        <BorderBeam
          duration={10}
          size={200}
          reverse
          className="from-transparent via-primary to-transparent"
        />
      </DrawerContent>
    </Drawer>
  ))

  // For layered layout
  if (layout === 'layered') {
    return (
      <>
        <section className="relative h-[600px] w-full overflow-hidden rounded-xl lg:rounded-3xl">
          <img
            src={media || '/placeholder.svg'}
            alt={localizedHeading || 'Background image'}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45"></div>
          <div className={cn('absolute inset-0 flex flex-col p-8 text-white md:p-12', layoutClass)}>
            <div className="max-w-md">
              <h2 className="text-[clamp(1.25rem,3vw,2.25rem)] font-bold leading-[1.1] dark:text-foreground md:max-xl:tracking-tighter">
                {localizedHeading}{' '}
              </h2>
              {description && (
                <p className="mb-6 line-clamp-4 md:line-clamp-6">{localizedDescription}</p>
              )}
              {hasDetails && (
                <div className="pt-4 lg:pb-4 xl:pt-8">
                  <button
                    className="rounded-md bg-primary px-4 py-1 text-white transition-colors hover:bg-primary/90 lg:px-6 lg:py-2"
                    onClick={() => handleReadMoreClick()}
                  >
                   {t('continueReading')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
        <ReadMoreBlock />
      </>
    )
  }

  // For split layout
  return (
    <>
      <section className="mx-auto w-full max-w-[1120px] rounded-2xl bg-white/20 p-4 backdrop-blur-xl dark:bg-background/20 md:bg-transparent md:p-0 lg:rounded-3xl">
        <div
          className={cn(
            'flex flex-col gap-[clamp(1.5rem,4vw,3rem)]',
            'md:items-start',
            layoutClass
          )}
        >
          <div className="h-full w-full shrink-0 rounded-xl md:w-[640px] md:shrink md:overflow-hidden lg:rounded-3xl">
            <img
              src={media || '/placeholder.svg'}
              alt={heading.en || 'Content image'}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0 self-center md:pt-4">
            <ContentBlock />
          </div>
        </div>
      </section>
      <ReadMoreBlock />
    </>
  )
}
